import { API_URL } from "../constants/constants";
import he from "he";

const GET_POSTS_URL = `${API_URL}/posts/all`;

interface DynamoDBAttribute<T> {
  S?: string; // string type in DynamoDB
  N?: string; // number type in DynamoDB (represented as a string)
  M?: Record<string, DynamoDBAttribute<any>>; // map type
}

interface ApiVelocityPost {
  post_caption: DynamoDBAttribute<string>;
  post_id: DynamoDBAttribute<string>;
  post_location_coords: DynamoDBAttribute<
    Record<string, DynamoDBAttribute<any>>
  >;
  post_location_name: DynamoDBAttribute<string>;
  post_timestamp: DynamoDBAttribute<string>;
  post_url: DynamoDBAttribute<string>;
  user_name: DynamoDBAttribute<string>;
  user_email: DynamoDBAttribute<string>;
  project_name: DynamoDBAttribute<string>;
  approved: DynamoDBAttribute<string>;
  upload_timestamp: DynamoDBAttribute<string>;
}

export interface ApiResponse {
  items: ApiVelocityPost[];
}

export interface Location {
  lat: number;
  lng: number;
}

// friendly interface for sunset item
export interface SunsetItem {
  sunsetCaption: string;
  sunsetId: string;
  sunsetLocationCoords: Location;
  sunsetLocationName: string;
  sunsetTimestamp: number;
  sunsetUrl: string;
  userName: string;
  userEmail: string;
  projectName: string;
  approved: string;
  uploadTimestamp: number;
  index: number;
}

export enum Status {
  APPROVED = "APPROVED",
  PENDING = "PENDING",
  REJECTED = "REJECTED",
};

export const getDisplaySunsets = async (payload?: object): Promise<SunsetItem[]> => {
  try {
    const response = await fetch(GET_POSTS_URL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: ApiResponse = await response.json();
    try {
      const posts: SunsetItem[] = result.items.map((item) => ({
        sunsetCaption: he.decode(item.post_caption.S ?? ""),
        sunsetId: item.post_id.S!!,
        sunsetLocationCoords: {
          lat: parseFloat(item.post_location_coords.M!!.lat.N!!),
          lng: parseFloat(item.post_location_coords.M!!.lng.N!!),
        },
        sunsetLocationName: he.decode(item.post_location_name.S ?? ""),
        sunsetTimestamp: parseInt(item.post_timestamp.N!!, 10),
        sunsetUrl: item.post_url.S ?? "",
        userName: he.decode(item.user_name.S ?? ""),
        index: -1, // assign after sorting
        approved: item.approved.S ?? "",
        userEmail: he.decode(item.user_email.S ?? ""),
        projectName: he.decode(item.project_name.S ?? ""),
        uploadTimestamp: parseInt(item.upload_timestamp.N!!, 10),
      }));
      posts.sort(
        (a, b) => a.sunsetLocationCoords.lng - b.sunsetLocationCoords.lng
      );
      posts.forEach((item, index) => {
        item.index = index;
      });
      return posts;
    } catch (error) {
      console.error("error parsing response:", error);
      throw error;
    }
  } catch (error) {
    console.error("error invoking Lambda:", error);
    throw error;
  }
};

export const getAdminSunsets = async (accessCode: string): Promise<SunsetItem[]> => {
  try {
    const response = await fetch(GET_POSTS_URL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-Access-Code": accessCode,
      },
    });

    if (!response.ok) {
      throw new Error(`error! status: ${response.status}`);
    }

    const result: ApiResponse = await response.json();
    try {
      const posts: SunsetItem[] = result.items.map((item) => ({
        sunsetCaption: he.decode(item.post_caption.S ?? ""),
        sunsetId: item.post_id.S!!,
        sunsetLocationCoords: {
          lat: parseFloat(item.post_location_coords.M!!.lat.N!!),
          lng: parseFloat(item.post_location_coords.M!!.lng.N!!),
        },
        sunsetLocationName: he.decode(item.post_location_name.S ?? ""),
        sunsetTimestamp: parseInt(item.post_timestamp.N!!, 10),
        sunsetUrl: item.post_url.S ?? "",
        userName: he.decode(item.user_name.S ?? ""),
        index: -1, // assign after sorting
        approved: item.approved.S ?? "",
        userEmail: he.decode(item.user_email.S ?? ""),
        projectName: he.decode(item.project_name.S ?? ""),
        uploadTimestamp: parseInt(item.upload_timestamp.N!!, 10),
      }));
      posts.sort(
        (a, b) => a.uploadTimestamp - b.uploadTimestamp
      );
      posts.forEach((item, index) => {
        item.index = index + 1;
      });
      return posts;
    } catch (error) {
      console.error("error parsing response:", error);
      throw error;
    }
  } catch (error) {
    console.error("error invoking Lambda:", error);
    throw error;
  }
};
